import { createSelector } from 'reselect'
import { slugify } from 'lib/slugify'
import { capitalizeString, slugifyPath, buildSchemaObjects } from 'lib/helpers'
import { getPageData, getPageParams } from 'selectors/page'
import { getSelectedFacetNameFirstAttribute } from 'selectors/algolia2/facets'
import { getAlgoliaDataHitsTotal } from 'selectors/algolia2/data'
import { getSearch } from 'selectors/algolia2/url'
import { getPagePath } from './app'
import { getPrintTypeText, hasPrintsSelected, getFacetValuesUrlParams } from './facets'
import { prettyCategory, stringCleanup } from '../helpers'
import { checkIfUrlShouldBeIndexed } from '../helpers/metadata'

export const getSeoContent = createSelector(
  [getPageData],
  (data): Record<string, any> => data.seoContent || {}
)

export const getUrlPathHasOutdoor = createSelector(
  [getPagePath],
  (pagePath): boolean => pagePath.includes('outdoor')
)

export const getMetaImage = createSelector(
  [getSelectedFacetNameFirstAttribute('category'), getUrlPathHasOutdoor],
  (category, urlPathHasOutdoor): string => {
    if (category === 'Sculpture') {
      if (urlPathHasOutdoor) {
        return 'https://images.saatchiart.com/saatchi-general/static/img/saatchi_art_outdoor_sculpture_og_image.jpg'
      }
      return 'https://images.saatchiart.com/saatchi-general/static/img/saatchi_art_sculpture_og_image.jpg'
    }
    return ''
  }
)

export const getSearchQuery = createSelector([getPageParams], (data): string|null => data.query || null)
// We only load 1 seo value on initial load
// We want to check if we have more then one value, else we need to fetch all
export const getCheckSeoContentLoaded = createSelector(
  [getSeoContent],
  (seoContent): boolean => Boolean(Object.keys(seoContent).length > 1) || false // Check more then 1 result
)
export const getSeoContentForCurrentPage = createSelector(
  [
    getPagePath,
    getFacetValuesUrlParams,
    getSeoContent,
    hasPrintsSelected,
    getSelectedFacetNameFirstAttribute('category'),
  ],
  (pagePath, urlParams, seoContent, hasPrints, category): Record<string, any> => {
    if (seoContent[pagePath]) {
      // If we can pick up the seoContent directly using the pagePath
      // Let's use that right away to avoid unnecessary logic
      return seoContent[pagePath]
    }
    let newPath = slugifyPath(urlParams)

    // if PRINTS then we want to drop CATEGORY
    // /prints/all || /prints/paintings || /prints/photography => /prints
    // /prints/all/modern || /prints/sculpture/modern => /prints/modern
    if (hasPrints && urlParams?.[1] === category) {
      urlParams.splice(1, 1) // Remove Category, there should always be a category

      newPath = slugifyPath(urlParams)
    }

    return seoContent[newPath] || {}
  }
)
export const getSeoMetadata = createSelector(
  [getSeoContentForCurrentPage],
  (seoContent): Record<string, any> =>
    seoContent
      ? {
          ...seoContent,
          h1: seoContent.copy_title || '',
          title: seoContent.meta_title || '',
          keywords: seoContent.meta_keywords === null ? null : seoContent.meta_keywords || '',
          description: seoContent.meta_description || '',
          tileRow: (seoContent.tile_row || []).filter(Boolean),
          intro: seoContent.copy_intro || '',
          // COUCHEBASE has some fields as `related_searches` || `related searches` || `Related Searches`, lets standarize
          sections: (seoContent.sections || [])
            .filter(Boolean)
            .map((i) => i.replace(/\s+/g, '_').toLowerCase()),
          relatedSearchesHeader: seoContent.section_header_related_searches || '',
          relatedSearchesCopy: seoContent.section_copy_related_searches || '',
        }
      : {}
)
export const hasReleatedSearches = createSelector(
  [getSeoContentForCurrentPage],
  (seoMetadata): boolean =>
    seoMetadata.sections && seoMetadata.sections.includes('related_searches')
)
export const getShortTitle = createSelector(
  [
    getSelectedFacetNameFirstAttribute('category'),
    getSelectedFacetNameFirstAttribute('styles'),
    getSelectedFacetNameFirstAttribute('mediums'),
    getSelectedFacetNameFirstAttribute('subject'),
    getSelectedFacetNameFirstAttribute('materials'),
    hasPrintsSelected,
  ],
  (category, style, medium, subject, material, hasPrints): string => {
    let title = ''
    title += ` ${[style, subject, medium, prettyCategory(category)].join(' ')}`
    // Check if no Value | Empty String
    const printTitle = !stringCleanup(title) ? 'Prints' : `Prints ${title}`
    const originalTitle = !stringCleanup(title) ? 'Original Artworks' : `Original ${title}`
    // Reset for Prints || Original
    title = hasPrints ? printTitle : originalTitle

    // Material
    if (material) {
      title += ` on ${material}`
    }

    return stringCleanup(title)
  }
)
export const getTitle = createSelector(
  [
    getSeoMetadata,
    getSelectedFacetNameFirstAttribute('artist_recognition'),
    getSelectedFacetNameFirstAttribute('category'),
    getSelectedFacetNameFirstAttribute('styles'),
    getSelectedFacetNameFirstAttribute('mediums'),
    getSelectedFacetNameFirstAttribute('subject'),
    getSelectedFacetNameFirstAttribute('country'),
    getSelectedFacetNameFirstAttribute('materials'),
    getSelectedFacetNameFirstAttribute('size'),
    getPrintTypeText,
    getSearch,
  ],
  (
    seoMetadata,
    artistRecognition,
    category,
    style,
    medium,
    subject,
    country,
    material,
    size,
    printTypeText,
    search
  ): string => {
    if (seoMetadata.h1) {
      return seoMetadata.h1
    }

    let title = printTypeText ? '' : 'Original'

    // Search will clear all facets
    if (search && typeof search === 'string') {
      title += ` ${capitalizeString(search)}`
    }

    const capitalizedSize = capitalizeString(size)

    title += ` ${[capitalizedSize, style, subject, medium].join(' ')}`

    if (printTypeText) {
      title += ` ${printTypeText}`
    } else {
      // Category || all => Art
      title += ` ${prettyCategory(category)}`
    }

    // Material
    if (material) {
      title += ` on ${material}`
    }

    // Country
    if (country) {
      title += ` From ${capitalizeString(country)}`
    }

    title += ' For Sale'

    // Artist Recognition
    if (artistRecognition) {
      title += ` By Artists Featured In ${artistRecognition.replace(
        /^Invest In Art$/gi,
        'Rising Stars'
      )} `
    }

    return stringCleanup(title)
  }
)
export const getSeoMetadataTitle = createSelector(
  [getSeoMetadata, getTitle],
  (seoMetadata, title): string => {
    if (seoMetadata.title) {
      return seoMetadata.title
    }

    return title
  }
)
export const getSeoMetadataDescription = createSelector(
  [
    getSeoMetadata,
    getSelectedFacetNameFirstAttribute('artist_recognition'),
    getSelectedFacetNameFirstAttribute('category'),
    getSelectedFacetNameFirstAttribute('styles'),
    getSelectedFacetNameFirstAttribute('mediums'),
    getSelectedFacetNameFirstAttribute('materials'),
    getSelectedFacetNameFirstAttribute('subject'),
    getSelectedFacetNameFirstAttribute('country'),
    hasPrintsSelected,
    getPrintTypeText,
  ],
  (
    seoMetadata,
    artistRecognition,
    category,
    style,
    medium,
    material,
    subject,
    country,
    hasPrints,
    printType
  ): string =>
    (seoMetadata.description
      ? seoMetadata.description
      : stringCleanup(
          `Shop ${style} ${subject} ${medium} ${hasPrints ? printType : category} ${
            material ? `on ${material}` : ''
          } created by thousands of emerging artists from ${
            country ? `${capitalizeString(country)}` : `around the world`
          }${
            artistRecognition
              ? ` featured in ${artistRecognition.replace(/^Invest In Art$/gi, 'Rising Stars')}`
              : ''
          }. Buy original art worry free with our 14 day satisfaction guarantee.`
        )
    ).replace(/\s+/g, ' ')
)
export const getIsIndexAllowed = createSelector(
  [getAlgoliaDataHitsTotal, getPagePath],
  (hitsTotal, urlPath): boolean => {
    const shouldBeIndexed = checkIfUrlShouldBeIndexed(urlPath)
    return shouldBeIndexed && hitsTotal > 10
  }
)
export const getBreadcrumbSchema = createSelector(
  [getFacetValuesUrlParams],
  (breadcrumbPaths): Object => {
    // Satisfies flow null check
    if (!breadcrumbPaths?.length) {
      return {}
    }

    // We deprecated LIMITED_SITE_URL in other areas of the site, so removed here.
    if (!process.env.SITE_URL) {
      throw new Error('Please define SITE_URL in .env')
    }

    let position = 1
    const siteUrl = process.env.SITE_URL
    const pathsWithoutNulls = breadcrumbPaths.filter(Boolean)
    const pathsCopy = [...pathsWithoutNulls]
    const category = pathsCopy.shift()
    const slugifiedPaths = pathsCopy.map((path) => slugify(path))

    // If showing all artworks and no other attributes
    // are selected, use 'All Artworks' for the name
    // Otherwise use the main category (Paintings, Drawings, etc)
    const categoryName =
      category === 'All' && pathsWithoutNulls.length === 1 ? 'All Artworks' : category

    // First json object 'All Artworks'
    const schema = {
      '@context': 'http://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [] as Array<{
        type: string
        position: number
        item: { '@id': string; name: string }
      }>,
    }

    if (categoryName !== 'All Artworks') {
      schema.itemListElement.push({
        type: 'ListItem',
        position: position,
        item: {
          '@id': `${siteUrl}/all`,
          name: 'All Artworks',
        },
      })
      position++
    }

    const removeAllFromMultipleAttr =
      pathsWithoutNulls.length > 1 && categoryName !== 'All Artworks'

    // Push the second json obj to the schema if:
    // -- Only one attribute
    // -- Multiple attributes but category is not 'all',
    if (pathsWithoutNulls.length === 1 || removeAllFromMultipleAttr) {
      schema.itemListElement.push({
        type: 'ListItem',
        position: position,
        item: {
          '@id': `${siteUrl}/${slugify(category)}`,
          name: `${categoryName.replace('-', ' ').replace('prints', 'Art Prints')}`,
        },
      })
    }

    // Pass the scoped args, map over the paths
    // and return an array of json objects
    const buildPathObj = pathsCopy.map((item: string, index: number) => {
      position++
      return buildSchemaObjects(index, position, category, siteUrl, slugifiedPaths, pathsCopy)
    })

    // Combine the existing json obj array with the one we created above
    schema.itemListElement = [...schema.itemListElement, ...buildPathObj]
    return schema
  }
)
export const getMetaData = createSelector(
  [
    getSearchQuery,
    getSeoMetadataTitle,
    getSeoMetadataDescription,
    getBreadcrumbSchema,
    getIsIndexAllowed,
    getMetaImage,
  ],
  (searchQuery, browseTitle, description, breadcrumb, isIndexAllowed, metaImage): Record<string, any> => {
    const title = `${searchQuery || browseTitle} | Saatchi Art`
    const ogData: Record<string, any> = {
      description,
      title,
      type: 'article',
    }

    // Add image to og data if available
    if (metaImage) {
      ogData.image = metaImage
    }

    return {
      description,
      title,
      og: ogData,
      preloadImages: [],
      robots: true,
      // robots: isIndexAllowed,
      schema: {
        breadcrumb,
        webpage: {
          headline: title,
          description,
        },
      },
    }
  }
)
